import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button } from "@pondigitalsolutions/pon-component-library";
import { ChevronDown } from "untitledui-js/react";

import { Alert } from "@components/Alert";
import { Pill } from "@components/Pill";
import {
  AUTO_TRANSLATE_LANGUAGE_OPTIONS,
  AutoTranslateLanguageMenu,
} from "@components/ControlCenter/AutoTranslateLanguageMenu";
import { useDropdownToggle } from "@hooks/useDropdownToggle";
import { useUserContext } from "@hooks/useUserContext";
import { useUpdatePreference } from "@hooks/useUpdatePreference";
import { useCurrentLang } from "@hooks/useCurrentLang";

interface LanguageWarningProps {
  labelText: string;
  message: string;
  pillAriaLabel: string;
  changeLanguageText: string;
  dropdownId: string;
  testId: string;
}

export const LanguageWarning = ({
  labelText,
  message,
  pillAriaLabel,
  changeLanguageText,
  dropdownId,
  testId,
}: LanguageWarningProps) => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const { user } = useUserContext();
  const { mutate } = useUpdatePreference();
  const menuRef = useRef<HTMLDivElement>(null);
  const { isOpen, toggle, close } = useDropdownToggle(menuRef);

  const currentLang = useCurrentLang(lang);
  const settingsHref = currentLang === "en" ? "/en/settings" : "/settings";

  const autoTranslateLanguage = user?.autoTranslateLanguage ?? null;
  const languageLabel = AUTO_TRANSLATE_LANGUAGE_OPTIONS.find(
    ({ code }) => code === autoTranslateLanguage
  )?.label;

  const rightSlot = user ? (
    <>
      <div ref={menuRef} className="relative hidden lg:flex">
        <Pill
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-controls={dropdownId}
          aria-label={pillAriaLabel}
          className="hidden lg:flex"
          icon={<ChevronDown width={12} />}
        >
          {languageLabel ?? changeLanguageText}
        </Pill>
        <AutoTranslateLanguageMenu
          id={dropdownId}
          isOpen={isOpen}
          language={autoTranslateLanguage}
          onSelect={(code) => {
            mutate({ autoTranslateLanguage: code, autoTranslateEnabled: true });
            close();
          }}
          className="top-10 right-auto left-0"
        />
      </div>
      <Button
        id="settings-button"
        variant="primary"
        size="sm"
        endIcon="ChevronRight"
        href={settingsHref}
      >
        {t("Settings.allSettings")}
      </Button>
    </>
  ) : undefined;

  return (
    <div data-testid={testId} className="mb-4 flex w-full justify-center">
      <Alert
        labelText={labelText}
        text={message}
        className="w-full max-w-none"
        textClassName="text-tertiary-900 dark:text-tertiary-100"
        rightSlot={rightSlot}
      />
    </div>
  );
};
